import { Check, Eye, FileText, ShieldAlert } from 'lucide-react'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AppShell } from '@/components/px/AppShell'
import { navText } from '@/components/px/navText'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { asApiError } from '@/services/apiError'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { DensityNote } from '@/components/px/DensityNote'
import { KpiCard } from '@/components/px/KpiCard'
import { PhaseRibbon } from '@/components/px/PhaseRibbon'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { CLIENT_CONTACT, isClientSide, personaFor } from '@/demo/data'
import {
  CLIENT_OWED,
  OPEN_DECISIONS,
  OPEN_DECISION_REFS,
  PORTAL_HEADER,
  PORTAL_PROGRESS,
  SIGNED_GATES,
  UAT_START,
} from '@/demo/content/portal'
import { selectRequirements } from '@/features/demo/demoSlice'
import { selectActiveOrgId, selectActiveRoleKey } from '@/features/auth/slices/sessionSlice'
import { healthTone, isoDayLabel, phaseDisplayName } from '@/features/projects/format'
import {
  fetchMyClient,
  selectMyClient,
  selectMyClientBlocked,
  selectMyClientError,
} from '@/features/my-work/slices/myWorkSlice'
import { useAppDispatch, useAppSelector } from '@/store'

const GATE_RECORDS_ID = 'gate-records'

/**
 * The REAL client landing (PX-W6.3, AC-6): the same `my-client-projects`
 * projection `/my/client` renders, in portal furniture. A firm-side caller
 * sees the typed side refusal here too — the portal has no chooser. Below it,
 * the deck's concept sections stay behind the badge (fixture-fed, zero
 * network).
 */
function ClientLanding() {
  const { t } = useTranslation('myWork')
  const dispatch = useAppDispatch()
  const orgId = useAppSelector(selectActiveOrgId)
  const client = useAppSelector(selectMyClient)
  const blocked = useAppSelector(selectMyClientBlocked)
  const error = useAppSelector(selectMyClientError)

  useEffect(() => {
    if (orgId !== null) void dispatch(fetchMyClient(orgId))
  }, [dispatch, orgId])

  if (blocked !== null) {
    return (
      <div role="alert" data-testid="client-landing-blocked" className="mb-6 rounded-lg border border-border bg-card px-5 py-6 text-center">
        <ShieldAlert className="mx-auto h-5 w-5 text-muted-foreground" aria-hidden />
        <p className="mt-2 font-medium text-foreground">{t('client.blockedTitle')}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t('client.blockedBody')}</p>
        <p className="mt-1 text-sm text-ink-soft">
          <span className="v-figure mr-1.5 font-semibold tabular-nums">{blocked.status}</span>
          {blocked.code !== undefined && <span className="mr-1.5 font-mono text-xs">{blocked.code}</span>}
          {blocked.message}
        </p>
      </div>
    )
  }

  if (error !== null) {
    /* PX-W6.6 (AC-2): the SHARED error panel, carrying the mapped KEY. */
    return (
      <div className="mb-6">
        <ErrorPanel
          error={asApiError(error)}
          onRetryClicked={() => orgId !== null && void dispatch(fetchMyClient(orgId))}
        />
      </div>
    )
  }

  if (client === null) {
    /* Loading is a STATE, not a blank frame: the portal announces the wait. */
    return (
      <div className="mb-6">
        <LoadingPanel label={t('client.loading')} />
      </div>
    )
  }

  return (
    <section aria-label={t('portal.title')} className="mb-8" data-testid="portal-client-landing">
      <SectionLabel>{t('client.title', { client: client.client.name })}</SectionLabel>
      <div className="mt-2 grid gap-2">
        {client.projects.length === 0 ? (
          <EmptyPanel title={t('client.emptyTitle')} body={t('client.emptyBody')} />
        ) : (
          client.projects.map((row) => (
            <Link
              key={row.id}
              to={`/project/${row.id}`}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-border bg-card px-4 py-3 hover:border-ring"
            >
              <span className="text-sm font-medium text-foreground">{row.name}</span>
              <StatusPill tone="neutral">{phaseDisplayName(row.currentPhase)}</StatusPill>
              <StatusPill tone={healthTone(row.health)}>{row.health ?? '—'}</StatusPill>
              <span className="ml-auto text-xs text-muted-foreground">
                {row.office ?? '—'} · {row.vertical ?? '—'} ·{' '}
                {row.goLiveDate ? isoDayLabel(row.goLiveDate) : '—'}
              </span>
            </Link>
          ))
        )}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{t('client.scopeLine')}</p>
    </section>
  )
}

/**
 * CLIENT PORTAL (route `/portal`) — the deck's open question "client portal as
 * module 4", built out as a CONCEPT. The deck names it on every project screen
 * and argues for it on the closing slide ("four in five delay days sit on the
 * client side") but never draws it, so every element here is designed from one
 * rule and nothing else: a client sees GATES, DECISIONS, UAT and PROGRESS —
 * never raw agent output, another client, or cost rates (deck 0.3).
 *
 * What that rule removes from this page, deliberately: budget used, contract
 * value, person rates, overnight agent runs, the internal Build and Migrate
 * gates as client actions, and every sibling project. What it keeps is the
 * client's own debt to the project — which is the whole argument for the
 * module. No left rail: the client has one project, so a nav would be a rail
 * with one destination.
 *
 * Figures come from `@/demo/content/portal` (which imports the core universe);
 * the requirement counter reads the live store, so a merge on an internal
 * screen moves the client's number too.
 */
export default function ClientPortalPage() {
  const { t: tPortal } = useTranslation(['myWork', 'nav'])
  const currentRole = useAppSelector(selectActiveRoleKey)
  /*
   * The breadcrumb names the caller's OWN client, from the live my-client
   * landing — it used to render `PORTAL_HEADER.client`, the fixture's company,
   * as the persistent chrome of a page whose data half is real (fix-back #2,
   * the same class as the project rail).
   */
  const landing = useAppSelector(selectMyClient)
  const requirements = useAppSelector(selectRequirements)

  const viewerName = personaFor(currentRole).name
  const viewingAsStaff = !isClientSide(currentRole)

  return (
    <AppShell
      moduleLabel={`PX · ${navText(tPortal, 'item.clientPortal', 'Client portal')}`}
      breadcrumb={landing?.client.name ?? tPortal('client.portalBreadcrumb')}
      contextPill="CURATED VIEW"
    >
      <div className="w-full">
        {viewingAsStaff && (
          <p className="mb-5 flex items-center gap-2 rounded-md border border-border bg-muted px-3 py-2 text-sm text-ink-soft">
            <Eye className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <span>
              You are viewing the client&rsquo;s curated portal as {viewerName} — this is what{' '}
              {PORTAL_HEADER.client} sees.
            </span>
          </p>
        )}

        <header>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-serif text-[30px] font-semibold leading-9 text-foreground">
              {PORTAL_HEADER.title}
            </h1>
            <ConceptBadge />
          </div>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {PORTAL_HEADER.health} · go-live {PORTAL_HEADER.goLiveLabel} · phase{' '}
            {PORTAL_HEADER.phaseName}, {PORTAL_HEADER.phaseNumber} of {PORTAL_HEADER.phaseCount}
          </p>
        </header>

        <ClientLanding />

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <ConceptBadge />
          <p className="max-w-2xl text-xs text-muted-foreground">
            {tPortal('portal.demoNote')}
          </p>
        </div>

        <PhaseRibbon current={PORTAL_HEADER.currentPhase} className="mt-5" />

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard
            label="Requirements approved"
            figure={`${requirements.approved} of ${requirements.total}`}
            qualifier={`${requirements.open} still open with your team`}
          />
          <KpiCard
            label="Your open decisions"
            figure={OPEN_DECISIONS.length}
            qualifier={`${OPEN_DECISION_REFS} — both waiting on you`}
          />
          <KpiCard
            label="UAT starts"
            figure={UAT_START.label}
            qualifier={`${UAT_START.weeksAway} weeks away · dates not confirmed`}
          />
          <KpiCard
            label="Go-live"
            figure={PORTAL_HEADER.goLiveLabel}
            qualifier={`${PORTAL_HEADER.health} · ${PORTAL_HEADER.goLiveWeeksAway} weeks`}
          />
        </div>

        <section aria-labelledby="owed-heading" className="mt-8">
          <div className="flex items-baseline justify-between gap-3">
            <SectionLabel>
              <span id="owed-heading">What we need from you</span>
            </SectionLabel>
            <span className="text-xs text-muted-foreground">
              {CLIENT_OWED.length} open · the same items, ids and dates your project team sees
            </span>
          </div>
          <ul className="mt-2 rounded-lg border border-border bg-card">
            {CLIENT_OWED.map((item) => (
              <li key={item.id} className="v-row px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill tone={item.tone}>{item.stateLabel}</StatusPill>
                  <span className="text-sm font-medium text-foreground">{item.title}</span>
                  <span className="text-xs text-muted-foreground">{item.askedLabel}</span>
                </div>
                <p className="mt-1 text-sm text-ink-soft">{item.detail}</p>
                {item.pills.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {item.pills.map((pill) => (
                      <StatusPill key={pill} tone="neutral">
                        {pill}
                      </StatusPill>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="gates-heading" className="mt-8">
          <SectionLabel>
            <span id="gates-heading">Gates you have signed</span>
          </SectionLabel>
          <ul className="mt-2 rounded-lg border border-border bg-card">
            {SIGNED_GATES.map((gate) => (
              <li key={gate.phaseKey} className="v-row flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-3">
                <span className="text-sm font-medium text-foreground">
                  {gate.phaseName} · {gate.subLabel}
                </span>
                <StatusPill tone="decided">
                  <Check className="h-3 w-3" aria-hidden />
                  Signed by {gate.signedBy} · {gate.signedOn}
                </StatusPill>
                <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                  {gate.released}
                </span>
                <a
                  href={`#${GATE_RECORDS_ID}`}
                  className="shrink-0 text-xs font-medium text-accent-foreground hover:underline"
                >
                  view record →
                </a>
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="progress-heading" className="mt-8">
          <SectionLabel>
            <span id="progress-heading">Progress, in your terms</span>
          </SectionLabel>
          <dl className="mt-2 grid grid-cols-1 gap-3 lg:grid-cols-3">
            {PORTAL_PROGRESS.map((line) => (
              <div key={line.label} className="rounded-lg border border-border bg-card px-4 py-3">
                <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  {line.label}
                </dt>
                <dd>
                  <div className="v-figure mt-1 font-serif text-[22px] font-semibold leading-7 tabular-nums text-foreground">
                    {line.figure}
                  </div>
                  <p className="mt-1 text-sm text-ink-soft">{line.detail}</p>
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <section
          id={GATE_RECORDS_ID}
          aria-labelledby="records-heading"
          tabIndex={-1}
          className="mt-8 scroll-mt-6"
        >
          <SectionLabel>
            <span id="records-heading">Gate records</span>
          </SectionLabel>
          <ul className="mt-2 rounded-lg border border-border bg-card">
            {SIGNED_GATES.map((gate) => (
              <li key={gate.phaseKey} className="v-row flex items-start gap-3 px-4 py-3">
                <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">{gate.artefact}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {gate.phaseName} gate · signed by {gate.signedBy} ({CLIENT_CONTACT.role},{' '}
                    {CLIENT_CONTACT.org}) on {gate.signedOn}
                  </div>
                  <p className="mt-1 text-sm text-ink-soft">{gate.released}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <DensityNote>
          This portal shows only what has been approved for {PORTAL_HEADER.client}: the gates{' '}
          {CLIENT_CONTACT.name} has signed, the decisions and change requests waiting on her, the UAT
          window and delivery progress. It withholds everything else the project holds — budget and
          contract value, the rates and names of who is staffed, overnight agent output before a
          person has reviewed it, the internal {PORTAL_HEADER.phaseName} and Migrate gates, and every
          other client. Each figure above is the project&rsquo;s own board record, not a separate
          client report: the requirement counter, the decision ids and the lateness are the same
          numbers the delivery team reads.
        </DensityNote>
      </div>
    </AppShell>
  )
}
